import React from 'react';
import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { closeTopOverlayAndHandleHistory, OverlayType, selectTextSearchOverlayVisible } from 'store/slice/overlay';
import { SearchResult, SuggestionResult } from 'core/type/SuggestionResult.types';
import { selectSearchBoxSettingsTextSearch, selectSearchBoxSettingsTravelDate, selectTextForTextSearch, setTextSearchInSearchBoxSettingsState } from 'store/slice/home';
import { routeTo, selectPageTypeId } from 'store/slice/navigation';
import { getSearchPageUrlFromGlobalKeyword, getSearchPageUrl } from 'core/utils/activityUrlGenerator';
import { selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap, selectGeoFromBootstrap, selectTopDestinationFromBootstrap } from 'store/slice/bootstrap';
import { CmsProps } from 'core/type/BootstrapConfig.types';
import { isOn } from 'core/window/bootstrapConfig';
import { TopDestination } from 'core/type/ActivitiesTopDestinations.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { FeatureName } from 'core/constant/FeatureName';

interface OwnProps {
    inputRef?: React.RefObject<HTMLInputElement>;
    isNewHomePageEnabled?: boolean;
}

interface StateProps {
    pageTypeId: number;
    isTextSearchOverlayVisible: boolean;
    searchText: string;
    suggestionResult?: SuggestionResult;
    travelDate?: Date;
    isNearByEnabled: boolean;
    isEmptyTextSearchAble: boolean;
    topDestinations?: TopDestination[];
}

interface DispatchProps {
    onSearchButtonClick: (result: SuggestionResult, travelDate?: Date) => void;
    onEmptyTextSearchButtonClick: (result: SearchResult) => void;
    onTextSearchOverlayClose: () => void;
    setSelectedResult: (selected?: SuggestionResult) => void;
    onSearchResultSelect: (result: SuggestionResult) => void;
    onGlobalKeywordResultSelect: (keyword: string) => void;
}

export type SearchBoxContainerProps = OwnProps & StateProps & DispatchProps & CmsProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectPageTypeId,
    selectGeoFromBootstrap,
    selectSearchBoxSettingsTextSearch,
    selectTextForTextSearch,
    selectTextSearchOverlayVisible,
    selectSearchBoxSettingsTravelDate,
    selectTopDestinationFromBootstrap,
    selectFeatureBehaviorFromBootstrap,
    (config, pageTypeId, geo, selectedSuggestion, searchText, isTextSearchOverlayVisible, travelDate, topDestinations, featureBehaviours): StateProps => {
        return {
            pageTypeId,
            isTextSearchOverlayVisible,
            searchText,
            travelDate,
            topDestinations,
            suggestionResult: selectedSuggestion || geo,
            isNearByEnabled: featureBehaviours.SearchBoxNearMeFeature,
            isEmptyTextSearchAble: isOn(FeatureName.HomepageTopDestinationSearchButton, config),
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    onTextSearchOverlayClose: () => {
        dispatch(closeTopOverlayAndHandleHistory({ overlay: OverlayType.TextSearch, shouldGoBack: true }));
    },
    onSearchButtonClick: (result: SuggestionResult, travelDate?: Date) => {
        dispatch(setTextSearchInSearchBoxSettingsState(result));
        const searchPageUrl = getSearchPageUrl(result, travelDate);
        dispatch(routeTo(searchPageUrl));
    },
    onEmptyTextSearchButtonClick: (result: SearchResult) => {
        dispatch(routeTo(result.url));
    },
    setSelectedResult: (selected?: SuggestionResult) => {
        dispatch(setTextSearchInSearchBoxSettingsState(selected));
    },
    onSearchResultSelect: (suggestion: SuggestionResult) => {
        if (!suggestion) return;
        const searchPageUrl = getSearchPageUrl(suggestion);
        dispatch(setTextSearchInSearchBoxSettingsState(suggestion));
        dispatch(routeTo(searchPageUrl));
    },
    onGlobalKeywordResultSelect: (keyword: string) => {
        const searchPageUrl = getSearchPageUrlFromGlobalKeyword(keyword);
        dispatch(routeTo(searchPageUrl));
    },
});

export const mapTopDestinationToSearchResult = (result: TopDestination): SearchResult => {
    return {
        cityName: result.cityName,
        cityId: result.cityId,
        type: TextSearchType.City,
        url: result.cityUrl,
    };
};
